// src/services/LivenessDetector.js
import * as faceapi from 'face-api.js';

export default class LivenessDetector {
  constructor(videoEl, canvasEl, onComplete) {
    this.video = videoEl;
    this.canvas = canvasEl;
    this.ctx = canvasEl.getContext('2d');
    this.onComplete = onComplete;

    this.sequence = [
      { action: 'lookRight', threshold: 25,  status: 'Vire a cabeça para a direita' },
      { action: 'lookLeft',  threshold: -25, status: 'Vire a cabeça para a esquerda' },
      { action: 'center',    threshold: 0,   status: 'Centralize o rosto e olhe para a câmera', wait: 4000 }
    ];
    this.currentStep = 0;
    this.waiting = false;
    this.waitStart = 0;
    this.singleFaceStartTime = null;
    this.requiredStableTime = 800; // 1.5 segundos de estabilidade
    this.currentInstruction = null;
    this.currentInstructionColor = '#1f2937';

    // === ANIMAÇÃO DA BARRA ===
    this.targetProgress = 0;
    this.currentProgress = 0;
    this.animationId = null;

    this.detectionInterval = null;
  }

  async start() {
    this.canvas.width = this.video.videoWidth;
    this.canvas.height = this.video.videoHeight;

    // Reset total
    this.currentStep = 0;
    this.waiting = false;
    this.targetProgress = 0;
    this.currentProgress = 0;

    this.detectionInterval = setInterval(() => this.detectLiveness(), 66);
  }

  drawFinalInstruction() {
    if (this.currentInstruction) {
      this.drawInstruction(this.currentInstruction, this.currentInstructionColor);
    }
  }

  async detectLiveness() {
    const detections = await faceapi
      .detectAllFaces(this.video, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
      .withFaceLandmarks();

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // === Nenhum rosto detectado ===
    if (!detections?.length) {
      this.currentInstruction = "Centralize seu rosto na câmera";
      this.currentInstructionColor = "#1f2937";
      this.drawFinalInstruction();
      return;
    }

    // === Mais de um rosto detectado ===
    if (detections.length > 1) {
      this.currentInstruction = "Apenas uma pessoa deve aparecer na câmera";
      this.currentInstructionColor = "#ef4444";
      this.drawFinalInstruction();
      return;
    }

    const detection = detections[0];

    if (!detection.landmarks?.positions) {
      this.currentInstruction = "Detectando rosto...";
      this.currentInstructionColor = "#3b82f6";
      this.drawFinalInstruction();
      return;
    }

    // === Brilho da imagem ===
    const brightness = this.calculateFaceBrightness(detection);
    if (brightness < 40 || brightness > 200) {
      this.currentInstruction = brightness < 40
        ? "Ambiente muito escuro. Aproxime-se da luz."
        : "Luz muito forte. Evite luz direta no rosto.";
      this.currentInstructionColor = "#ef4444";
      this.drawFinalInstruction();
      return;
    }

    // === Redimensiona e espelha ===
    const resized = faceapi.resizeResults(detections, {
      width: this.canvas.width,
      height: this.canvas.height
    })[0];

    const mirroredBox = {
      x: this.canvas.width - (resized.detection.box.x + resized.detection.box.width),
      y: resized.detection.box.y,
      width: resized.detection.box.width,
      height: resized.detection.box.height
    };

    const mirroredLandmarks = {
      ...resized.landmarks,
      positions: resized.landmarks.positions.map(p => ({
        x: this.canvas.width - p.x,
        y: p.y
      }))
    };

    new faceapi.draw.DrawBox(mirroredBox, { boxColor: '#10b981', lineWidth: 3 }).draw(this.canvas);
    new faceapi.draw.DrawFaceLandmarks(mirroredLandmarks, {
      lineWidth: 2, pointSize: 2, color: '#a855f7'
    }).draw(this.canvas);

    const step = this.sequence[this.currentStep];
    const now = Date.now();

    // === Movimento: direita / esquerda ===
    if (step.action === 'lookRight' || step.action === 'lookLeft') {
      const yaw = this.calculateYaw(detection.landmarks);
      if ((step.action === 'lookRight' && yaw > step.threshold) ||
          (step.action === 'lookLeft' && yaw < step.threshold)) {
        this.advanceStep();
      }

      this.currentInstruction = step.status;
      this.currentInstructionColor = '#1f2937';
      this.drawProgressBar();
      this.drawFinalInstruction();
      return;
    }

    // === Centralizar rosto ===
    if (step.action === 'center') {
      if (!this.waiting) {
        this.waiting = true;
        this.waitStart = now;
        this.singleFaceStartTime = null;
      }

      const elapsed = now - this.waitStart;
      const remaining = Math.max(0, step.wait - elapsed);
      const seconds = Math.ceil(remaining / 1000);

      // Enquanto conta tempo
      if (remaining > 0) {
        this.currentInstruction = step.status;
        this.currentInstructionColor = '#1f2937';
        this.drawCountdownCircle(seconds, step.wait / 1000);
        this.drawFinalInstruction();
        return;
      }

      // // === Verificação final antes da captura ===
      // const finalDetections = await faceapi
      //   .detectAllFaces(this.video, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
      //   .withFaceLandmarks();

      // if (!finalDetections?.length) {
      //   this.currentInstruction = "Nenhum rosto detectado — mantenha-se visível.";
      //   this.currentInstructionColor = "#ef4444";
      //   this.waitStart = Date.now();
      //   this.singleFaceStartTime = null;
      //   this.drawFinalInstruction();
      //   return;
      // }

      // if (finalDetections.length > 1) {
      //   this.currentInstruction = "Mais de uma pessoa detectada. Faça novamente sozinho(a).";
      //   this.currentInstructionColor = "#ef4444";
      //   this.waitStart = Date.now();
      //   this.singleFaceStartTime = null;
      //   this.drawFinalInstruction();
      //   return;
      // }

      // // === Aguardando estabilidade ===
      // const nowStable = Date.now();
      // if (!this.singleFaceStartTime) {
      //   this.singleFaceStartTime = nowStable;
      //   this.currentInstruction = "Aguardando estabilidade...";
      //   this.currentInstructionColor = "#3b82f6";
      //   this.drawFinalInstruction();
      //   return;
      // }

      // const stableDuration = nowStable - this.singleFaceStartTime;
      // if (stableDuration < this.requiredStableTime) {
      //   this.currentInstruction = "Aguardando estabilidade...";
      //   this.currentInstructionColor = "#3b82f6";
      //   this.drawFinalInstruction();
      //   return;
      // }

      // === Captura final ===
      this.flashScreen();

      const capturedCanvas = document.createElement('canvas');
      capturedCanvas.width = this.canvas.width;
      capturedCanvas.height = this.canvas.height;
      const cctx = capturedCanvas.getContext('2d');
      cctx.drawImage(this.video, 0, 0, capturedCanvas.width, capturedCanvas.height);

      capturedCanvas.toBlob(blob => {
        if (!blob) {
          console.error('Falha ao gerar blob');
          this.onComplete(null, null, brightness);
          return;
        }
        this.onComplete(capturedCanvas, blob, brightness);
        this.stop();
      }, 'image/jpeg', 0.9);
    }

    this.drawFinalInstruction();
  }


  // === AVANÇA ETAPA + ANIMAÇÃO COM DELAY NO 100% ===
  advanceStep() {
    // const previousStep = this.currentStep;
    this.currentStep++;
    this.waiting = false;

    this.targetProgress = (this.currentStep / 2) * 100;

    if (this.currentStep === 2) {
      // === 100% — ANIMA E ESPERA ANTES DE MUDAR ===
      this.animateProgressBar();

      setTimeout(() => {
        if (this.currentStep === 2 && !this.waiting) {
          this.waiting = true;
          this.waitStart = Date.now();
        }
      }, 900); // 600ms animação + 300ms pausa
    } else {
      this.animateProgressBar();
    }
  }

  // === ANIMAÇÃO SUAVE DA BARRA ===
  animateProgressBar() {
    const easeOutQuad = (t) => 1 - Math.pow(1 - t, 2);
    const duration = 600;
    const startTime = Date.now();
    const startProgress = this.currentProgress;
    const endProgress = this.targetProgress;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuad(progress);

      this.currentProgress = startProgress + (endProgress - startProgress) * eased;
      this.drawProgressBar(this.currentProgress);

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
        this.currentProgress = this.targetProgress;
        this.drawProgressBar();
      }
    };

    if (this.animationId) cancelAnimationFrame(this.animationId);
    this.animationId = requestAnimationFrame(animate);
  }

  // === BARRA DE PROGRESSO (SÓ ATÉ 100%) ===
  drawProgressBar(progress = this.currentProgress) {
    if (this.currentStep > 2) return;

    const barWidth = this.canvas.width - 100;
    const barX = 50;
    const barY = 100;

    this.ctx.fillStyle = '#e5e7eb';
    this.ctx.fillRect(barX, barY, barWidth, 10);

    this.ctx.fillStyle = '#10b981';
    this.ctx.fillRect(barX, barY, barWidth * (progress / 100), 10);

    this.ctx.strokeStyle = '#9ca3af';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(barX, barY, barWidth, 10);
  }

  // === CÍRCULO NO CANTO INFERIOR DIREITO ===
  drawCountdownCircle(seconds, totalSeconds = 2) {
    const radius = 40;
    const lineWidth = 6;
    const centerX = this.canvas.width - radius - 20;
    const centerY = this.canvas.height - radius - 20;

    const progress = (totalSeconds - seconds) / totalSeconds;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + progress * 2 * Math.PI;

    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    this.ctx.strokeStyle = '#e5e7eb';
    this.ctx.lineWidth = lineWidth;
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, radius, startAngle, endAngle);
    this.ctx.strokeStyle = '#10b981';
    this.ctx.lineWidth = lineWidth;
    this.ctx.stroke();

    this.ctx.font = '32px sans-serif';
    this.ctx.fillStyle = '#1f2937';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(seconds, centerX, centerY);
  }

  // === FLASH NA TELA ===
  flashScreen() {
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    requestAnimationFrame(() => {
      this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
    });
  }

  // === YAW (68 landmarks) ===
  calculateYaw(landmarks) {
    const p = landmarks.positions;
    const noseTip = p[30];
    const leftJaw = p[1];
    const rightJaw = p[15];

    const centerX = noseTip.x;
    const leftDist = centerX - leftJaw.x;
    const rightDist = rightJaw.x - centerX;

    const yaw = (rightDist - leftDist) * 0.4;
    return Math.max(-50, Math.min(50, yaw));
  }

  // === BRILHO ===
  calculateFaceBrightness(detection) {
    const box = detection.detection.box;
    const { width, height } = this.canvas;
    const x = Math.max(0, Math.floor(box.x));
    const y = Math.max(0, Math.floor(box.y));
    const w = Math.min(width - x, Math.floor(box.width));
    const h = Math.min(height - y, Math.floor(box.height));
    if (w <= 0 || h <= 0) return 0;

    const temp = document.createElement('canvas');
    temp.width = width; temp.height = height;
    const tctx = temp.getContext('2d');
    tctx.drawImage(this.video, 0, 0, width, height);
    const data = tctx.getImageData(x, y, w, h).data;
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.299 * data[i] + 0.587 * data[i+1] + 0.114 * data[i+2];
    }
    return sum / (data.length / 4);
  }

  // === INSTRUÇÃO NO TOPO ===
  drawInstruction(text, color = '#1f2937') {
    const ctx = this.ctx;
    const fontSize = 22;
    const lineHeight = 30;
    const paddingX = 16;
    const paddingY = 10;
    const startY = 30;

    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle'; // 👈 centraliza verticalmente o texto

    const lines = text.split('\n');
    const textWidths = lines.map(line => ctx.measureText(line).width);
    const boxWidth = Math.max(...textWidths) + paddingX * 2;
    const boxHeight = lines.length * lineHeight + paddingY * 2;

    const centerX = this.canvas.width / 2;
    const boxX = centerX - boxWidth / 2;
    const boxY = startY;

    // === Fundo branco arredondado ===
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 4;
    this.roundRect(ctx, boxX, boxY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // === Texto centralizado verticalmente ===
    ctx.fillStyle = color;
    const textStartY = boxY + boxHeight / 2 - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      const y = textStartY + i * lineHeight;
      ctx.fillText(line, centerX, y);
    });
  }

  roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  // === STOP ===
  stop() {
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
